/**
 * SDMS Template Builder — the EDITABLE design sheet.
 *
 * A dedicated Univer instance (not the Result Entry sheet, which is tuned for merged
 * batch data: cell locks, formula rewrites, Engine B columns). It loads the template
 * with the shared `excelToUniver` mapper in value-only mode, puts back the RAW
 * formulas read from the package (so the designer sees and edits the real text, not
 * a Result-Entry rewrite), and registers FIELD() so merge cells show `[NAME]`.
 *
 * Editing uses Univer's own ribbon: type text / formulas, font, fill, borders,
 * alignment, number format, merge, column / row size — plus our own Start → Conditional
 * Formatting ▾ (New Rule… / Manage Rules…), handed to the editor. Nothing is saved from here —
 * `getEdits()` diffs the sheet against the load-time snapshot and the editor sends
 * that to the server, which patches only those parts of the .xlsx.
 *
 * Fields dragged from the field list (MIME `application/x-sdms-field`) are dropped
 * onto the cell under the pointer.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createUniver, defaultTheme, LocaleType, merge } from '@univerjs/presets';
import { UniverSheetsCorePreset, CalculationMode } from '@univerjs/presets/preset-sheets-core';
import UniverPresetSheetsCoreEnUS from '@univerjs/presets/preset-sheets-core/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-core.css';
// Drawing preset = the range-anchored float DOM the template's chart preview sits in (as in SDMS Result Entry).
import { UniverSheetsDrawingPreset } from '@univerjs/presets/preset-sheets-drawing';
import UniverPresetSheetsDrawingEnUS from '@univerjs/presets/preset-sheets-drawing/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-drawing.css';
import { excelToUniver } from '../../analysis/SdmsUniverSheet';
import { dropCharts } from '../../analysis/sdmsChartStore';
import { floatWorkbookCharts } from '../../analysis/sdmsLiveCharts';
import { registerFieldFunction } from './fieldFunction';
import { diffSnapshots, EMPTY_EDITS, type SheetEdits, type USnapshot } from './sheetEdits';
import { parseA1, type WorkbookFormulaCell } from './templateWorkbook';

export const FIELD_DRAG_MIME = 'application/x-sdms-field';

export interface ActiveRange { sheetName: string; sheetIndex: number; startRow: number; startCol: number; endRow: number; endCol: number }

export interface TemplateSheetHandle {
  /** The current selection with its 1-based WORKBOOK sheet index; null when none. */
  getActiveRange: () => ActiveRange | null;
  /** Writes a formula into a cell (Map Field / drop). */
  setCellFormula: (sheetIndex: number, row: number, col: number, formula: string) => boolean;
  /** Selects a cell (e.g. from the Data Parsing grid). */
  selectCell: (sheetIndex: number, row: number, col: number) => void;
  /** What the designer changed since load. */
  getEdits: () => SheetEdits;
  /** Data-transfer editor "Upload Yaml File": clears row 1 of the ACTIVE sheet and writes the
   *  YAML's output columns there in bold (legacy LoadHeadersIntoSheet). Returns the sheet name. */
  writeHeaderRow: (headers: string[]) => string | null;
  /** "Load Instrument File": replaces the ACTIVE sheet's content with header (row 1, bold), an
   *  empty row 2 for the parsing mapping, and the parsed rows from row 3 (legacy LoadDataTableIntoSheet). */
  loadTable: (columns: string[], rows: string[][]) => string | null;
  /** Clears every row from `fromRow` (0-based) down on the named sheet (legacy StripInstrumentDataBeforeSave). */
  clearFromRow: (sheetName: string, fromRow: number) => void;
}

// Loose facade surface (the presets build types it generically).
type FRange = { setFormula?: (f: string) => unknown; getFormula?: () => string; activate?: () => unknown;
  getRange?: () => { startRow: number; startColumn: number; endRow: number; endColumn: number };
  setValues?: (v: string[][]) => unknown; setFontWeight?: (w: 'bold' | 'normal' | null) => unknown;
  clear?: () => unknown; clearContent?: () => unknown };
type FSheet = { getSheetName?: () => string; getRange?: (r: number, c: number, nr?: number, nc?: number) => FRange;
  getActiveRange?: () => FRange | null; activate?: () => unknown;
  getLastRow?: () => number; getLastColumn?: () => number };
type FWorkbook = { save?: () => unknown; getSnapshot?: () => unknown; getActiveSheet?: () => FSheet | null;
  getSheetByName?: (n: string) => FSheet | null; setEditable?: (v: boolean) => unknown; setActiveSheet?: (s: FSheet) => unknown };
type FMenu = { appendTo?: (path: string) => void; addSubmenu?: (m: FMenu) => FMenu;
  // Facade internals used by addConditionalFormattingMenu (see there for why).
  __getSchema?: () => Record<string, Record<string, unknown>>;
  _menuManagerService?: { mergeMenu?: (config: Record<string, unknown>) => void } };
type FApi = { createWorkbook: (d: unknown) => unknown; getActiveWorkbook?: () => FWorkbook | null;
  registerComponent?: (name: string, c: () => JSX.Element) => unknown;
  createMenu?: (m: { id: string; title: string; tooltip?: string; icon?: string; order?: number; action: () => void }) => FMenu;
  createSubmenu?: (m: { id: string; title: string; tooltip?: string; icon?: string }) => FMenu };

/** Clears a range's values and formatting (`clear`), else its values only. */
function clearRange(r: FRange) {
  if (r.clear) r.clear();
  else r.clearContent?.();
}

/** What the ribbon's Conditional Formatting menu asks for. */
export type ConditionalFormatCommand = 'new' | 'manage';

/**
 * Excel's Home → Conditional Formatting dropdown, in the sheet's own ribbon (Windows-app parity).
 *
 * It goes in the FORMAT group, not "others": Univer folds the ribbon's tail behind "more" when
 * the sheet is narrow (the field list takes a column), and "others" is the first to disappear.
 *
 * Univer 0.25's ribbon dropdown lists only items placed DIRECTLY under the dropdown and skips
 * group nodes, but the facade's `createSubmenu().addSubmenu()` always wraps its items in a
 * group — so the documented route renders an empty list. The two items are therefore merged
 * straight under the dropdown through the submenu's own menu manager. If that internal is
 * ever missing, it falls back to one plain button that opens the Rules Manager.
 */
/** One button on the sheet ribbon's "Template" tab. */
export interface TemplateRibbonAction {
  /** Stable id (also the command id Univer runs on click). */
  id: string;
  title: string;
  tooltip?: string;
  /** Tabler icon class, e.g. `ti-link`. */
  icon: string;
  run: () => void;
}

/**
 * A "Mail Merge" tab of our own in the sheet's ribbon (owner request 2026-10-01, renamed from "Template"
 * 2026-10-02 — the Windows builder keeps
 * its template actions in the spreadsheet ribbon too). Univer draws one tab per top-level node under
 * `ribbon` (captioned by its `title`), each holding groups of items, so the tab is merged in as
 * `ribbon.template` → `ribbon.template.mapping` → the buttons. Items are built with the facade's
 * `createMenu` and, as for Conditional Formatting, re-keyed so the item id equals its command id
 * (Univer runs the command named by the item's id).
 */
function addTemplateTab(api: FApi, actions: TemplateRibbonAction[], run: (id: string) => void) {
  if (!api.createMenu) return;
  const items: Record<string, unknown> = {};
  let merge: ((config: Record<string, unknown>) => void) | undefined;
  actions.forEach((a, order) => {
    const icon = `stb-tpl-icon-${a.id}`;
    // Univer's ribbon button renders ONLY the icon when an item has one, so the caption is drawn
    // inside the icon component — the buttons read "Map Field", "Set Header Band", … (owner request).
    api.registerComponent?.(icon, () => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 6px', whiteSpace: 'nowrap' }}>
        <i className={`ti ${a.icon}`} style={{ fontSize: 16 }} />{a.title}
      </span>
    ));
    const menu = api.createMenu!({ id: a.id, title: a.title, tooltip: a.tooltip ?? a.title, icon, order, action: () => run(a.id) });
    merge ??= menu._menuManagerService?.mergeMenu?.bind(menu._menuManagerService);
    const schema = menu.__getSchema?.();
    const node = schema?.[a.id] as { menuItemFactory?: (...x: unknown[]) => { commandId?: string } } | undefined;
    const factory = node?.menuItemFactory;
    if (!schema || !node || !factory) return;
    node.menuItemFactory = (...x: unknown[]) => { const m = factory(...x); return { ...m, id: m.commandId ?? a.id }; };
    Object.assign(items, schema);
  });
  if (!merge || Object.keys(items).length !== actions.length) {
    // Facade internals missing — fall back to the Start tab's "others" group so nothing is lost.
    for (const a of actions)
      api.createMenu({ id: `${a.id}-fallback`, title: a.title, tooltip: a.tooltip ?? a.title, action: () => run(a.id) })
        .appendTo?.('ribbon.start.others');
    return;
  }
  merge({ ribbon: { 'ribbon.template': { order: 10, title: 'Mail Merge', 'ribbon.template.mapping': { order: 0, ...items } } } });
}

/**
 * A ribbon dropdown with "New Rule…" and "Manage Rules…" (Conditional Formatting, Data
 * Validation). Falls back to a single button opening the manager when the facade internals
 * the dropdown needs are missing.
 */
function addRulesMenu(api: FApi, o: { id: string; title: string; managerTitle: string; iconClass: string; group: string; run: (cmd: ConditionalFormatCommand) => void }) {
  if (!api.createSubmenu || !api.createMenu) return;
  const icon = `${o.id}-icon`;
  api.registerComponent?.(icon, () => <i className={`ti ${o.iconClass}`} style={{ fontSize: 16 }} />);
  const item = (id: string, caption: string, cmd: ConditionalFormatCommand, order: number) =>
    api.createMenu!({ id, title: caption, action: () => o.run(cmd), order });

  const dropdown = api.createSubmenu({ id: o.id, title: o.title, tooltip: o.title, icon });
  const own = dropdown.__getSchema?.()[o.id];
  const merge = dropdown._menuManagerService?.mergeMenu?.bind(dropdown._menuManagerService);
  // Picking a dropdown item runs the command named by the item's ID (not its commandId —
  // built-in items use one value for both), so the id is aligned with the registered command.
  const entry = (id: string, caption: string, cmd: ConditionalFormatCommand, order: number) => {
    const schema = item(id, caption, cmd, order).__getSchema?.();
    const node = schema?.[id] as { menuItemFactory?: (...a: unknown[]) => { commandId?: string } } | undefined;
    const factory = node?.menuItemFactory;
    if (!schema || !node || !factory) return undefined;
    node.menuItemFactory = (...a: unknown[]) => { const m = factory(...a); return { ...m, id: m.commandId ?? id }; };
    return schema;
  };
  const newRule = entry(`${o.id}-new`, 'New Rule…', 'new', 0);
  const manage = entry(`${o.id}-manage`, 'Manage Rules…', 'manage', 1);
  if (own && merge && newRule && manage) {
    for (const k of Object.keys(own)) if (k.startsWith(`${o.id}-group-`)) delete own[k];
    merge({ [o.group]: { [o.id]: { ...own, ...newRule, ...manage } } });
    return;
  }
  api.createMenu({ id: `${o.id}-button`, title: o.title, tooltip: o.managerTitle, icon, action: () => o.run('manage') })
    .appendTo?.(o.group);
}

const addConditionalFormattingMenu = (api: FApi, run: (cmd: ConditionalFormatCommand) => void) =>
  addRulesMenu(api, { id: 'stb-cf', title: 'Conditional Formatting', managerTitle: 'Conditional Formatting Rules Manager',
    iconClass: 'ti-color-filter', group: 'ribbon.start.format', run });

/** Data ▸ Rules — where Excel and Univer keep Data Validation (2026-10-12). */
const addDataValidationMenu = (api: FApi, run: (cmd: ConditionalFormatCommand) => void) =>
  addRulesMenu(api, { id: 'stb-dv', title: 'Data Validation', managerTitle: 'Data Validation Rules Manager',
    iconClass: 'ti-checklist', group: 'ribbon.data.rules', run });

export const TemplateSheetDesigner = forwardRef<TemplateSheetHandle, {
  buf: ArrayBuffer;
  /** Worksheet names in WORKBOOK order (hidden ones included) — sheet index = position + 1. */
  sheetNames: string[];
  /** Raw formulas read from the package (templateWorkbook). */
  formulas: WorkbookFormulaCell[];
  readOnly?: boolean;
  onReady?: () => void;
  onFieldDropped?: (at: { sheetIndex: number; row: number; col: number }, payload: string) => void;
  /** The ribbon's Conditional Formatting menu (New Rule… / Manage Rules…). */
  onConditionalFormat?: (cmd: ConditionalFormatCommand) => void;
  /** The Data tab's Data Validation menu (New Rule… / Manage Rules…). */
  onDataValidation?: (cmd: ConditionalFormatCommand) => void;
  /** Buttons for the sheet ribbon's own "Template" tab (Map Field, bands, …). Read when the sheet is
   *  built; each `run` is looked up again on click, so the latest handler is always used. */
  templateActions?: TemplateRibbonAction[];
}>(function TemplateSheetDesigner({ buf, sheetNames, formulas, readOnly, onReady, onFieldDropped, onConditionalFormat, onDataValidation, templateActions }, ref) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const cfRef = useRef(onConditionalFormat);
  cfRef.current = onConditionalFormat;
  const dvRef = useRef(onDataValidation);
  dvRef.current = onDataValidation;
  const actionsRef = useRef(templateActions);
  actionsRef.current = templateActions;
  const apiRef = useRef<FApi | null>(null);
  const baselineRef = useRef<USnapshot | null>(null);
  const namesRef = useRef(sheetNames);
  namesRef.current = sheetNames;
  const [error, setError] = useState<string | null>(null);

  const indexOf = (name: string) => namesRef.current.indexOf(name) + 1;
  const sheetOf = (sheetIndex: number): FSheet | null =>
    apiRef.current?.getActiveWorkbook?.()?.getSheetByName?.(namesRef.current[sheetIndex - 1] ?? '') ?? null;
  const snapshot = (): USnapshot | null => {
    const wb = apiRef.current?.getActiveWorkbook?.();
    return (wb?.save?.() ?? wb?.getSnapshot?.() ?? null) as USnapshot | null;
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    let univerRef: { dispose?: () => void } | null = null;
    // Chart ids are unique per designer instance, so a reopened template never shows a stale chart.
    const chartPrefix = `stb-chart-${Math.random().toString(36).slice(2, 10)}`;
    const container = document.createElement('div');
    container.style.cssText = 'width:100%;height:100%';
    host.appendChild(container);

    void (async () => {
      try {
        const mapped = await excelToUniver(buf, false);
        if (disposed) return;
        const data = mapped.data as { sheetOrder?: string[]; sheets?: Record<string, { name?: string; cellData?: Record<number, Record<number, Record<string, unknown>>> }> };
        // Put the RAW formulas back (value-only mode dropped them; the cached value stays for display).
        for (const id of data.sheetOrder ?? []) {
          const sheet = data.sheets?.[id];
          const idx = sheet?.name ? indexOf(sheet.name) : 0;
          if (!sheet || idx < 1) continue;
          const cellData = (sheet.cellData ??= {});
          for (const f of formulas.filter(x => x.sheetIndex === idx)) {
            const at = parseA1(f.ref);
            if (!at) continue;
            const cell = ((cellData[at.row] ??= {})[at.col] ??= {});
            cell.f = f.formula;
          }
        }

        const { univer, univerAPI } = createUniver({
          locale: LocaleType.EN_US,
          locales: { [LocaleType.EN_US]: merge({}, UniverPresetSheetsCoreEnUS, UniverPresetSheetsDrawingEnUS) },
          theme: defaultTheme,
          presets: [UniverSheetsCorePreset({
            container,
            header: true, toolbar: true, formulaBar: true, contextMenu: true,
            // Keep the cached values the file carries; compute only cells that have none.
            formula: { initialFormulaComputing: CalculationMode.WHEN_EMPTY },
            // The drawing preset is here only for the chart preview: a picture inserted in the browser
            // would not reach the .xlsx (only cell edits are saved), so its Insert ▸ Image is hidden.
            menu: { 'sheet.menu.image': { hidden: true } },
          } as Parameters<typeof UniverSheetsCorePreset>[0]), UniverSheetsDrawingPreset()],
        });
        univerRef = univer as unknown as { dispose?: () => void };
        if (disposed) { univerRef.dispose?.(); return; }
        const api = univerAPI as unknown as FApi;
        apiRef.current = api;
        api.createWorkbook(mapped.data);
        registerFieldFunction(univer);                  // AFTER createWorkbook (see fieldFunction.ts)
        if (!readOnly && cfRef.current) addConditionalFormattingMenu(api, cmd => cfRef.current?.(cmd));
        if (!readOnly && dvRef.current) addDataValidationMenu(api, cmd => dvRef.current?.(cmd));
        if (!readOnly && actionsRef.current?.length)
          addTemplateTab(api, actionsRef.current, id => actionsRef.current?.find(a => a.id === id)?.run());
        const wb = api.getActiveWorkbook?.();
        if (readOnly) wb?.setEditable?.(false);
        baselineRef.current = snapshot();
        void floatWorkbookCharts(api, buf, mapped.data, chartPrefix);
        onReady?.();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[SDMS Template Builder] could not open the workbook', e);
        if (!disposed) setError('The workbook could not be opened in the designer.');
      }
    })();

    return () => {
      disposed = true;
      apiRef.current = null;
      baselineRef.current = null;
      try { univerRef?.dispose?.(); } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SDMS Template Builder] dispose failed', e);
      }
      dropCharts(chartPrefix);
      container.remove();
    };
    // The workbook is (re)built only when a new file arrives; names/formulas come with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buf, readOnly]);

  const activeRange = (): ActiveRange | null => {
    const sh = apiRef.current?.getActiveWorkbook?.()?.getActiveSheet?.();
    const rg = sh?.getActiveRange?.()?.getRange?.();
    const sheetName = sh?.getSheetName?.();
    if (!rg || !sheetName) return null;
    const sheetIndex = indexOf(sheetName);
    if (sheetIndex < 1) return null;
    return { sheetName, sheetIndex, startRow: rg.startRow, startCol: rg.startColumn, endRow: rg.endRow, endCol: rg.endColumn };
  };

  useImperativeHandle(ref, () => ({
    getActiveRange: activeRange,
    setCellFormula: (sheetIndex, row, col, formula) => {
      const range = sheetOf(sheetIndex)?.getRange?.(row, col, 1, 1);
      if (!range?.setFormula) return false;
      range.setFormula(formula);
      return true;
    },
    selectCell: (sheetIndex, row, col) => {
      const wb = apiRef.current?.getActiveWorkbook?.();
      const sh = sheetOf(sheetIndex);
      if (!wb || !sh) return;
      sh.activate?.();
      sh.getRange?.(row, col, 1, 1)?.activate?.();
    },
    writeHeaderRow: headers => {
      const sh = apiRef.current?.getActiveWorkbook?.()?.getActiveSheet?.();
      if (!sh?.getRange || headers.length === 0) return null;
      const width = Math.max(headers.length, (sh.getLastColumn?.() ?? -1) + 1);
      clearRange(sh.getRange(0, 0, 1, width));
      const row = sh.getRange(0, 0, 1, headers.length);
      row.setValues?.([headers]);
      row.setFontWeight?.('bold');
      return sh.getSheetName?.() ?? null;
    },
    loadTable: (columns, rows) => {
      const sh = apiRef.current?.getActiveWorkbook?.()?.getActiveSheet?.();
      if (!sh?.getRange || columns.length === 0) return null;
      const lastRow = sh.getLastRow?.() ?? -1, lastCol = sh.getLastColumn?.() ?? -1;
      if (lastRow >= 0 && lastCol >= 0) clearRange(sh.getRange(0, 0, lastRow + 1, lastCol + 1));   // overwrite, as legacy
      const header = sh.getRange(0, 0, 1, columns.length);
      header.setValues?.([columns]);
      header.setFontWeight?.('bold');
      if (rows.length > 0) sh.getRange(2, 0, rows.length, columns.length).setValues?.(rows.map(r => columns.map((_, i) => r[i] ?? '')));
      return sh.getSheetName?.() ?? null;
    },
    clearFromRow: (sheetName, fromRow) => {
      const sh = apiRef.current?.getActiveWorkbook?.()?.getSheetByName?.(sheetName);
      if (!sh?.getRange) return;
      const lastRow = sh.getLastRow?.() ?? -1, lastCol = sh.getLastColumn?.() ?? -1;
      if (lastRow >= fromRow && lastCol >= 0) clearRange(sh.getRange(fromRow, 0, lastRow - fromRow + 1, lastCol + 1));
    },
    getEdits: () => {
      const base = baselineRef.current;
      const cur = snapshot();
      if (!base || !cur) return EMPTY_EDITS;
      return diffSnapshots(base, cur, indexOf, (name, r, c) => {
        const f = apiRef.current?.getActiveWorkbook?.()?.getSheetByName?.(name)?.getRange?.(r, c, 1, 1)?.getFormula?.();
        return f ? f : null;
      });
    },
  }));

  // ── drop a field onto the cell under the pointer ───────────────────────────
  const onDragOver = (e: React.DragEvent) => {
    if (readOnly || !e.dataTransfer.types.includes(FIELD_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDrop = (e: React.DragEvent) => {
    const payload = e.dataTransfer.getData(FIELD_DRAG_MIME);
    if (readOnly || !payload) return;
    e.preventDefault();
    // Univer selects on pointer events; replay a click where the field was dropped, then
    // read the selection it produced (the canvas has no public hit-test in the facade).
    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (target) {
      const init = { bubbles: true, cancelable: true, clientX: e.clientX, clientY: e.clientY, button: 0, pointerId: 1, pointerType: 'mouse' };
      target.dispatchEvent(new PointerEvent('pointerdown', init));
      target.dispatchEvent(new PointerEvent('pointerup', init));
    }
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const r = activeRange();
      if (r) onFieldDropped?.({ sheetIndex: r.sheetIndex, row: r.startRow, col: r.startCol }, payload);
    }));
  };

  return (
    <div className="stb-designer" data-unsaved-ignore onDragOver={onDragOver} onDrop={onDrop}>
      {error ? <div className="stb-empty"><i className="ti ti-alert-triangle" /><p>{error}</p></div> : <div ref={hostRef} className="stb-designer-host" />}
    </div>
  );
});
